// ---------------------------------------------------------------------------
// ITEM 7 (2026-08-11) — **ONE MESSAGE, AND THE BUBBLE HOLDS ONLY THE MESSAGE.**
//
// Her report: *"the bubble stops being a thick block carrying everything: photo and name on top
// outside, text alone inside, actions and time below outside."* Her seven clauses, and which piece
// of this file answers which:
//
//   1. Sender photo and @name ABOVE and OUTSIDE the bubble   -> `.lr-msg__who`, above `.lr-msg__bubble`
//   2. like / delete / time-ago just BELOW, outside          -> `.lr-msg__foot`, after the bubble
//   3. Thin clean bubbles holding ONLY the message           -> `.lr-msg__bubble` and nothing else in it
//   4. ONE SHARED COMPONENT                                  -> this file
//   5. The frozen mobile palette untouched                   -> the sheet, and see the note below
//   6. Attachment preview inside the composer before send    -> **ALREADY SHIPPED as LR16(a)**, verified
//                                                               and locked rather than built twice (R19)
//   7. Videos tap open to the full-screen player             -> `MessageMedia` + `ImageViewer`
//
// **`.lr-msg` IS THE ROW NOW AND `.lr-msg__bubble` IS THE BUBBLE, AND THAT NAMING IS THE ROUND'S ONE
// STRUCTURAL DECISION.** The obvious build is the other way round — keep `.lr-msg` as the bubble and
// wrap it in a new outer class — and it walks straight into the trap this item was pre-measured for:
// L6(4)'s frozen palette is written as DESCENDANT selectors under `.lr-msg`
// (`.inbox-frame .lr-msg .lr-when`, `… .lr-seen`, `… .lr-tiny`, `… .lr-msg__who`), so lifting the
// who-line and the foot out of `.lr-msg` silently drops every one of them and her @name, her
// timestamp and her icons revert to a colour chosen for a different background — **her own reported
// defect arriving through the fix for it**, with a diff that reads as a tidy-up. Keeping the row
// under the same class means every one of those rules still MATCHES; what changes is the ground each
// element stands on, and that is dealt with deliberately in the sheet rather than by accident here.
//
// It also keeps twenty-seven scripts pointed at a real thing: `document.querySelectorAll(".lr-msg")`
// is still one node per message and its `textContent` is still the whole message, which is what
// `lr16-media-walk.mjs` — **on the wall** — reads it for.
//
// **IT IS HOOKLESS ON PURPOSE.** Every decision is a prop, so `scripts/item7-message-layout.mjs`
// CALLS it and reads the tree back (R7/R8): the who-line's presence, the photo, what is inside the
// bubble and what is outside it are all questions a gate can ask without a browser. A block inside a
// 1,200-line file could only ever be checked by regex over source.
import { memo } from "react";
import { Trash2, ThumbsUp } from "lucide-react";
import { Avatar, MentionBody, MessageMedia, OwnerCrown, ago, openMemberMenu, type Message } from "./LivingRoomShared";
import { SharedPostCard } from "./SharedPostCard";

// ---------------------------------------------------------------------------------------------
// PERF-1 — **THIS ROW IS MEMOIZED, AND THE PROP SIGNATURE BELOW IS WHAT MAKES THAT WORTH ANYTHING.**
//
// HER REPORT: the inbox still freezes on desktop after C4. C4 removed the QUADRATIC term (381,924
// array reads per keystroke) and **said in its own closing line that the linear half was still
// there**: one `MessageRow` per message per keystroke. Measured on her real dimensions — **698
// messages in Elite 2026** — every keystroke in the composer calls `setBody`, re-renders the inbox,
// and re-renders all 698 of these.
//
// >>> `React.memo` ALONE WOULD HAVE CHANGED NOTHING, AND THAT IS THE TRAP THIS ROUND HAD TO WALK
// PAST. <<< The call site passed THREE INLINE ARROWS per row (`onAskDelete`, `onOpenMedia`,
// `onOpenPost`), so every row received three brand-new function identities on every keystroke and a
// memo comparison would find them unequal every single time. **A wrapper that reads as a complete
// repair and skips nothing** — this house's most-paid shape, arriving as a performance fix.
//
// So `onOpenMedia` takes the MESSAGE as well as the index: the per-row closure moves INSIDE this
// component, where it is rebuilt only when this row really re-renders, and the parent can hold one
// stable callback for all 698 rows.
// ---------------------------------------------------------------------------------------------
function MessageRowInner({
  message: m, canLike, canDelete, seen, onLike, onAskDelete, onOpenMedia, onOpenPost,
  eagerMedia = true,
}: {
  message: Message;
  canLike: boolean;
  canDelete: boolean;
  // ITEM 10's mark, or `null` when this is not the row that wears it. The DECISION of which message
  // carries it is `receiptMessageId`'s and stays with the thread — "the last one" is exactly the kind
  // of thing that survives a refactor backwards into "the first one" with nothing on screen to notice.
  seen: string | null;
  onLike: (m: Message) => void;
  // NAMED FOR WHAT IT DOES (R35): this component cannot delete anything. It hands the id up and
  // the inbox opens the room's ONE confirm sheet, which is the only thing that POSTs the delete.
  onAskDelete: (id: string) => void;
  // PERF-1: the MESSAGE rides with the index so the parent can hold ONE stable callback for every
  // row rather than minting one closure per row per keystroke (see the note above).
  onOpenMedia: (m: Message, at: number) => void;
  onOpenPost: (postId: string) => void;
  // IT #124 — **THE THREAD DECIDES AND THE ROW OBEYS**, which is `seen`'s own shape one prop up:
  // where this row sits relative to the newest message is a fact the LIST holds, and a row holding
  // an opinion about its own position is the kind of thing that survives a refactor backwards with
  // nothing on screen to notice. It defaults to TRUE, so a caller that says nothing gets exactly
  // what shipped (ITEM 12's neutrality argument) and this row stays a pure function of its props.
  eagerMedia?: boolean;
}) {
  return (
    <div className={["lr-msg", m.mine ? "is-mine" : "", m.pending ? "is-pending" : ""].filter(Boolean).join(" ")}>
      {/* HER CLAUSE 1 — **EVERY MESSAGE, NOT ONLY SOMEBODY ELSE'S IN A GROUP.** The retired row drew
          a name only when `thread.group && !m.mine`, so a DM carried no attribution at all and her
          own words carried none anywhere. Her sentence is *"every message shows the sender's profile
          photo with their @name beside it"*, and it is read literally (DECISIONS ITEM7-1).

          **THE PHOTO IS FREE AND NOTHING HERE COMPOSES A URL.** `listMessages` selects `AVATAR_COLS`
          beside `author_name`, so `m.author` is a full `Identity` — ITEM 4c's biconditional
          guarantees it — and `Avatar` brings the house glyph for a member with no photograph, the
          owner ring, and the 404 fallback by EXISTING. A second way to draw a member is the exact
          thing ITEM 4c collapsed into one component. */}
      {/* N4 (her queue item 8, 2026-08-11) — **`lg`, THE SAME RUNG THE THREAD LIST USES**, which is
          her "consistent between list and thread" clause read literally. It was `xs` (20px) against
          the list's 36, so the two surfaces she named disagreed by nearly double and the smaller one
          was the one she is reading a conversation on. **N4's ROUND left the name beside it
          untouched, because HER N4 CLAUSE was about the PHOTOS** -- migrated at its declaration
          rather than left standing as a claim this file no longer holds (R141/DL-2): her ITEM 7
          below makes that name a DOOR, which is a different clause of hers and not a re-tune of
          this one. The SIZE is what N4 ruled on, and `lg` is untouched. */}
      {/* >>> HER ITEM 7: A PROFILE TAP MEANS THE SAME THING HERE AS IT DOES IN THE ROOM. <<< The
          face and the @name above a message were the last member identity in this house that did
          NOTHING when tapped -- the room's bylines, its comments and her live chat all open the
          follow-to-message box, and the surface where two people are actually talking did not. So
          which screen you happened to be on decided whether tapping somebody's picture worked.

          It routes through `openMemberMenu` and wears `.lr-who__face` / `.lr-who__name--door`
          rather than growing a handler and a look of its own, so Follow, Message, her block door and
          the tap target reach this row by EXISTING (R28/R29). The element is handed over, so the box
          opens under the face she tapped rather than in the corner.

          Her own seat needs no guard here: `MemberMenu` returns null for `self`, which is the one
          answer to *whose name is a door* and not a condition re-decided at this call site. */}
      <span className="lr-msg__who">
        <button type="button" className="lr-who__face" aria-label={`@${m.author.name}`}
          onClick={(e) => openMemberMenu(m.author, e.currentTarget)}>
          <Avatar who={m.author} size="lg" />
        </button>
        <button type="button" className="lr-msg__name lr-who__name--door"
          onClick={(e) => openMemberMenu(m.author, e.currentTarget)}>
          @{m.author.name}<OwnerCrown who={m.author} />
        </button>
      </span>
      {/* HER CLAUSE 3 — **ONLY THE MESSAGE IS IN HERE.** The body or the shared-post card, and the
          media that came with it. Her pictures are part of what was said, so they stay inside; the
          name above and the actions below do not.

          S6-3, HER RULING, OVERRULING LR16-e: **GONE IS GONE.** The tombstone is retired — a deleted
          message does not reach this list at all, because the STORE's own read carries
          `deleted_at IS NULL`. Nothing is filtered here, which is deliberate: a client-side hide
          would still have been SENT her words. */}
      <div className="lr-msg__bubble">
        {/* HER QC (2026-08-07) — **A SHARED POST RENDERS AS A CARD AND ITS RAW PATH NEVER REACHES HER
            SCREEN.** The card REPLACES the body rather than sitting beside it. The decision is the
            SERVER's — a message carries `sharedPost` only when the referenced post is one THIS VIEWER
            may see — so nothing here re-decides who may read what. */}
        {m.sharedPost
          ? <SharedPostCard post={m.sharedPost} onOpen={onOpenPost} />
          : (m.body || m.draftBody
            ? <MentionBody text={m.draftBody || m.body} mentions={m.mentions} className="lr-msg__body" />
            : null)}
        {/* LR16(b) as ITEM 7 left it — her pictures AND her video, inline, and a tap on either opens
            the round-nine lightbox. The index handed over is the index within the WHOLE strip now,
            because the viewer takes the whole strip; the old images-only arithmetic opened the wrong
            photograph whenever a video sat first.
            The media of a DELETED message is already gone from this payload — the store's ONE
            visibility fragment carries `deleted_at IS NULL` — so nothing is filtered a second time
            here, which would be a second answer key for who may see a picture. */}
        <MessageMedia media={m.media || []} onOpen={(at) => onOpenMedia(m, at)} eager={eagerMedia} />
      </div>
      {/* HER CLAUSE 2 — **THE ACTIONS AND THE STAMP SIT UNDER THE BUBBLE, OUTSIDE IT.** */}
      <span className="lr-msg__foot">
        {/* TONIGHT-2 — **A PENDING ROW OFFERS NOTHING TO PRESS**, and this surface gets it from the
            same `canActOn` the comment row asks (R28/R29: one law, not one per surface). */}
        {canLike ? (
          <button type="button" className={m.liked ? "lr-tiny is-on" : "lr-tiny"} onClick={() => onLike(m)} aria-label="Thumbs up">
            <ThumbsUp size={12} />{m.likes || ""}
          </button>
        ) : null}
        {/* LR16-e — **HER OWN MESSAGE, HER OWN BIN**, and it is the room's ONE confirm rather than a
            second one written for this screen. `canDelete` is the shared law and it reads
            `item.deleted`, so a message that is already gone offers nothing to press. */}
        {canDelete ? (
          <button type="button" className="lr-tiny" onClick={() => onAskDelete(m.id)} aria-label="Delete">
            <Trash2 size={12} />
          </button>
        ) : null}
        <span className="lr-when">{ago(m.createdAt)}</span>
      </span>
      {seen ? <span className="lr-seen">{seen}</span> : null}
    </div>
  );
}

// **THE MEMO, AND IT IS THE DEFAULT SHALLOW COMPARISON RATHER THAN A HAND-WRITTEN ONE.**
//
// A custom comparator here would be a SECOND declaration of what makes a row different, and the one
// that drifts is the one that stops re-rendering a row after a member edits it — her words frozen on
// screen with nothing red anywhere (R28/R29 on a surface where the failure is silent). React's own
// shallow compare over these eight props is exact: `message` is an object identity that only changes
// when the thread really changes, `canLike`/`canDelete`/`seen` are primitives, and all four handlers
// are held stable by the caller. **That last clause is the load-bearing one and it is held by
// `perf1-render-budget.mjs`**, because a caller that reverts to inline arrows turns this wrapper back
// into a comparison that never matches — with no symptom but a site that feels slow again.
export const MessageRow = memo(MessageRowInner);

// **AND THE PLAIN FUNCTION IS EXPORTED BESIDE IT, BECAUSE `memo()` RETURNS AN OBJECT AND A GATE
// CANNOT CALL AN OBJECT.** ITEM 7's whole instrument is that this component is HOOKLESS, so
// `item7-message-layout.mjs` CALLS it and reads the tree back — and the moment the export became a
// memo wrapper that fixture died with `mod.MessageRow is not a function`, which is a real cost of
// this repair rather than a detail. **The reach is kept rather than the memo dropped:** the two
// render byte-identical trees, because the wrapper adds nothing but a comparison, so a gate that
// calls the inner one is asking about exactly what a member sees.
export { MessageRowInner };
